// src/pages/PostcodeQuotenewMini.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PostcodeInput from "../components/PostcodeInput";
import api from "../utils/api";
import { trackFunnelEvent } from "../utils/funnelTracking";

// Extract a UK-style postcode if present; otherwise return trimmed input.
const extractPostcode = (text = "") => {
  const upper = text.toUpperCase();
  const match = upper.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/);
  if (match) return match[0].replace(/\s+/g, "");
  // Heathrow keyword fallback
  if (upper.includes("HEATHROW") || upper.includes("LHR")) return "TW6";
  return text.trim();
};

function placeIdFromPlace(place) {
  return place?.place_id || place?.placeId || null;
}

function buildLocationPayload(text) {
  const cleanedText = String(text || "").trim();
  if (!cleanedText) return "";
  return extractPostcode(cleanedText);
}

const PostcodeQuotenewMini = ({ presetPickup = "", presetDropoff = "" }) => {
  const navigate = useNavigate();
  const MIN_ENQUIRY_HOURS = 4;
  const DEFAULT_HOURS = 25; // 1 day + 1 hour

  const isoInHours = (h) => {
    const d = new Date(Date.now() + h * 60 * 60 * 1000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [fromPostcode, setFromPostcode] = useState(presetPickup);
  const [toPostcode, setToPostcode] = useState(presetDropoff);
  const [pickupPlaceId, setPickupPlaceId] = useState(null);
  const [dropoffPlaceId, setDropoffPlaceId] = useState(null);
  const [pickupTime, setPickupTime] = useState(isoInHours(DEFAULT_HOURS));
  const [errors, setErrors] = useState({});
  const [loadingQuote, setLoadingQuote] = useState(false);

  useEffect(() => {
    trackFunnelEvent("landing_view", {
      brand: "minibusgrab",
      source: "quote_widget",
    });
  }, []);

  useEffect(() => {
    setFromPostcode(presetPickup);
    setPickupPlaceId(null);
  }, [presetPickup]);
  useEffect(() => {
    setToPostcode(presetDropoff);
    setDropoffPlaceId(null);
  }, [presetDropoff]);

  const validate = () => {
    if (!fromPostcode.trim() || !toPostcode.trim()) {
      setErrors({ quote: "Please enter both pickup and drop-off postcodes." });
      return false;
    }
    if (fromPostcode.trim().length < 3 || toPostcode.trim().length < 3) {
      setErrors({ quote: "Please enter full pickup and drop-off postcodes (min 3 characters each)." });
      return false;
    }
    if (!pickupTime) {
      setErrors({ quote: "Please select a pickup date and time." });
      return false;
    }
    return true;
  };

  const getQuote = async () => {
    if (!validate()) return;

    setLoadingQuote(true);
    setErrors({});

    const pickup = buildLocationPayload(fromPostcode);
    const dropoff = buildLocationPayload(toPostcode);

    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup,
        dropoff,
        pickup_place_id: pickupPlaceId || undefined,
        dropoff_place_id: dropoffPlaceId || undefined,
        pickup_time: pickupTime,
        passengers: 8, // ensure minibus sizing so both 8- and 16-seaters are relevant
        bags: 0,
      });

      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        navigate(`/minibus-results/${data.quote_id}/${data.access_token}?vehicles=nine,fourteen`);
        return;
      }

      if (data.comingSoon) {
        setErrors({
          quote:
            "🚧 Sorry, we’re not yet operating full coverage for this route.<br/>" +
            "We’re expanding rapidly — please check back soon or try another nearby pickup area. 🚐",
        });
        return;
      }

      if (data.error) {
        setErrors({ quote: `⚠️ ${data.error}` });
        return;
      }

      setErrors({
        quote:
          "We’re temporarily unable to calculate a fare for this route.<br/>" +
          "Please verify your pickup and drop-off details or try again shortly.",
      });
    } catch (err) {
      console.error("❌ Error fetching fare:", err);
      setErrors({
        quote:
          err.response?.data?.error ||
          "⚠️ Our system is temporarily busy. Please try again in a few moments.",
      });
    } finally {
      setLoadingQuote(false);
    }
  };

  return (
    <div className="hero-form">
      <PostcodeInput
        placeholder="Pickup postcode"
        value={fromPostcode}
        onChange={(value) => {
          setFromPostcode(value);
          setPickupPlaceId(null);
        }}
        onPlaceSelected={(place) => {
          const val = place.formatted_address || place.name || "";
          setFromPostcode(val);
          setPickupPlaceId(placeIdFromPlace(place));
        }}
        restrictions={{ country: "uk" }}
      />

      <PostcodeInput
        placeholder="Drop-off postcode"
        value={toPostcode}
        onChange={(value) => {
          setToPostcode(value);
          setDropoffPlaceId(null);
        }}
        onPlaceSelected={(place) => {
          const val = place.formatted_address || place.name || "";
          setDropoffPlaceId(placeIdFromPlace(place));
          setToPostcode(val);
        }}
        restrictions={{ country: "uk" }}
      />

      <input
        type="datetime-local"
        value={pickupTime}
        onChange={(e) => {
          const minIso = isoInHours(MIN_ENQUIRY_HOURS);
          const next = e.target.value && e.target.value < minIso ? minIso : e.target.value;
          setPickupTime(next);
        }}
        min={isoInHours(MIN_ENQUIRY_HOURS)}
        className="hero-datetime"
      />

      <button type="button" className="hero-cta" onClick={getQuote} disabled={loadingQuote}>
        {loadingQuote ? "Calculating…" : "Get Minibus Quote"}
      </button>

      {errors.quote && (
        <div
          className="error-banner"
          style={{ gridColumn: "1 / -1", marginTop: 6 }}
          dangerouslySetInnerHTML={{ __html: errors.quote }}
        />
      )}
    </div>
  );
};

export default PostcodeQuotenewMini;
